import React, { useEffect } from "react"
import { QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter } from "react-router-dom"
import { Toaster } from "sonner"
import { queryClient } from "../lib/query-client"
import { api } from "../lib/api/client"
import { useUIStore, useAuthStore, useCallStore } from "../stores"
import { useMatterPreferencesStore } from "../features/matters/stores/matter-preferences-store"
import { useSocketConnection } from "../hooks"
import { getLoginUrl } from "../lib/auth"
import { DismissExclusionsProvider } from "../components/ui/dismiss-exclusions-context"
import { useOptimizePerformance } from "../hooks/useOptimizePerformance"
import { hydrateCurrentCallFromServer } from "../lib/call-center/current-call-sync"
import { installConsoleCapture } from "../lib/console-capture"
import { NavigationTracker } from "../lib/navigation-history"

// Install console capture once at module load
installConsoleCapture()

interface ProvidersProps {
  children: React.ReactNode
}

/**
 * Initializes app-level resources:
 * - Socket connection for real-time updates
 * - DOMAINS configuration from server
 */
function AppInitializer({ children }: { children: React.ReactNode }) {
  const theme = useUIStore((state) => state.theme)
  const setDomains = useUIStore((state) => state.setDomains)
  const setAuth = useAuthStore((state) => state.setAuth)
  const setInitialized = useAuthStore((state) => state.setInitialized)

  // Apply dark class to html element
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark")
  }, [theme])

  // Initialize socket connection
  useSocketConnection()

  // Sync optimize-performance class on <body> based on user setting
  useOptimizePerformance()

  // Load user and config on mount (skip for test pages)
  useEffect(() => {
    if (window.location.pathname.startsWith("/test")) {
      setInitialized()
      return
    }
    async function loadUserAndConfig() {
      try {
        // Load both in parallel (like original app does with Promise.all)
        // This ensures DOMAINS is available before any redirect happens
        const [userResult, configResult] = await Promise.all([
          api.auth.viewingUser(),
          api.load.standard(),
        ])

        // Set domains first (before any redirect can happen)
        if (configResult.data?.DOMAINS) {
          setDomains(configResult.data.DOMAINS)
        }

        // Now handle auth state
        if (userResult.success && userResult.data) {
          setAuth(userResult.data, "")
          // Initialize call center queue status from user data
          if (userResult.data.agent_is_in_queue) {
            useCallStore.getState().setIsInQueue(true)
          }
          // Recover active call state (e.g. after page refresh during a call)
          if (userResult.data.agent_current_call) {
            void hydrateCurrentCallFromServer("app-init")
          }
          // Initialize matter preferences from server setting (also syncs item filter store)
          useMatterPreferencesStore.getState().initFromServerSetting(
            userResult.data.matter_settings?.show_only_my_items ?? false
          )
        } else {
          // No valid user, mark as initialized but not authenticated
          // Safe to redirect now - domains is already loaded
          setInitialized()
        }
      } catch (err) {
        console.error("Failed to load app config:", err)
        // Mark as initialized even on error so the app can redirect to login
        setInitialized()
      }
    }
    loadUserAndConfig()
  }, [setDomains, setAuth, setInitialized])

  // Re-validate user session when window regains focus (skip for test pages)
  useEffect(() => {
    const handleFocus = async () => {
      if (window.location.pathname.startsWith("/test")) return

      try {
        const userResult = await api.auth.viewingUser()
        if (!userResult.success || !userResult.data) {
          // Session expired, redirect to login
          window.location.href = getLoginUrl()
        }
      } catch (err) {
        console.error("Failed to re-validate user session:", err)
      }
    }

    window.addEventListener("focus", handleFocus)
    return () => window.removeEventListener("focus", handleFocus)
  }, [])

  return <>{children}</>
}

export function Providers({ children }: ProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NavigationTracker />
        <DismissExclusionsProvider>
          <AppInitializer>
            {children}
            <Toaster position="top-right" richColors closeButton />
          </AppInitializer>
        </DismissExclusionsProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
